import { Container } from 'hypo-container';
import { getDecorators } from '@estee/elc-service';
import { IAppConfig, root } from '@estee/elc-universal-utils';
import { ELCLogger, TriggerType } from '@estee/elc-logging';
import { ServiceBusController } from './ServiceBusController';
import { ProdcatApiSdk } from '../api/ProdcatApiSdk';
import { ProductSkuRepository } from '../internal/repositories/ProductSkuRepository';
import { ZoomViewBaseController } from '../internal/controllers/ZoomViewBaseController';
import { ProductZoomViewController } from '../internal/controllers/ProductZoomViewController';
import { ProductZoomButtonViewController } from '../internal/controllers/ProductZoomButtonViewController';
import { HoverZoom } from '../internal/domain/entities/HoverZoom';
import { ProductHoverZoomViewController } from '../internal/controllers/ProductHoverZoomViewController';
import { ProductHoverZoomButtonViewController } from '../internal/controllers/ProductHoverZoomButtonViewController';
import { ProductHoverZoomItemViewController } from '../internal/controllers/ProductHoverZoomItemViewController';
import { ProductRepository } from '../internal/repositories/ProductRepository';
import { ProductBriefViewController } from '../internal/controllers/ProductBriefViewController';
import { ProductGridViewController } from '../internal/controllers/ProductGridViewController';
import { ProductCrossSellViewController } from '../internal/controllers/ProductCrossSellViewController';
import { CartLimitReachedModalController } from '../internal/controllers/CartLimitReachedModalController';
import { ProductCTAViewController } from '../internal/controllers/ProductCTAViewController';
import { ProductQuickViewController } from '../internal/controllers/ProductQuickViewController';
import { ProductShadesSidebarViewController } from '../internal/controllers/ProductShadesSidebarViewController';
import { FilterService } from '../internal/domain/services/FilterService';
import {
    UrlFormatter,
    PriceFormatter,
    DateFormatter,
    BusinessUnitIdParser
} from '@estee/elc-formatters';
import { Config } from '../internal/domain/entities/Config';
import { ConfigRepository } from '../internal/repositories/ConfigRepository';
import { ProductFiltersViewController } from '../internal/controllers/ProductFiltersViewController';
import { ProductGridSettings } from '../internal/domain/entities/ProductGridSettings';
import { QueryFragments } from '../api/QueryFragments';
import { ProductInstallmentsPriceViewController } from '../internal/controllers/ProductInstallmentsPriceViewController';
import { ProductEngravingViewController } from '../internal/controllers/ProductEngravingViewController';
import { ProductGridService } from '../internal/domain/services/ProductGridService';
import { ProductFullService } from '../internal/domain/services/ProductFullService';
import { ProductSortingService } from '../internal/domain/services/ProductSortingService';
import { ProductShadePickerViewController } from '../internal/controllers/ProductShadePickerViewController';
import { ProductShadePickerComponentViewController } from '../internal/controllers/ProductShadePickerComponentViewController';
import { ProductBasicInfoViewController } from '../internal/controllers/ProductBasicInfoViewController';
import { ProductFullViewController } from '../internal/controllers/ProductFullViewController';
import { ProductImageSliderViewController } from '../internal/controllers/ProductImagesSliderViewController';
import { ProductSizePickerViewController } from '../internal/controllers/ProductSizePickerViewController';
import { ComparisonItemViewController } from '../internal/controllers/ComparisonItemViewController';
import { ProductComparisonModuleViewController } from '../internal/controllers/ProductComparisonModuleViewController';
import { InventoryRepository } from '../internal/repositories/InventoryRepository';
import { StickyWrapperViewController } from '~controllers/StickyWrapperViewController';
import { SkuPickerViewController } from '~controllers/SkuPickerViewController';
import { ShadeFiltersViewController } from '~controllers/ShadeFiltersViewController';
import serviceNames, { ProductService } from './ProductService';
import { ProductServiceStartedUseCase } from '~use-cases/ProductServiceStartedUseCase';

const { name: serviceName, version } = __serviceInfo__;

export const diContainer = new Container();

export const setConfig = (configJSON: IAppConfig) => {
    diContainer.config = configJSON;
};

export async function serviceInitializer(config: IAppConfig) {
    setConfig(config);

    diContainer.get(serviceNames.serviceBusController);

    const productServiceStartedUseCase = new ProductServiceStartedUseCase([
        'currency',
        'itemMaxQuantity',
        'businessUnitId',
        'features',
        'drupal',
        'graphqlExtensionVersion',
        'imagesSizes',
        'imageCarouselSettings',
        'shortDescription',
        'viewableInventoryStatuses',
        'domainLangPrefix',
        'inventoryStatusSorting',
        'backorder',
        'prodcatInventory'
    ]);

    await productServiceStartedUseCase.execute();
}

diContainer
    .register(
        serviceNames.logger,
        () =>
            new ELCLogger({
                serviceName,
                serviceVersion: version,
                environment: root.env,
                buid: root.buid
            })
    )
    .register(
        serviceNames.productFullViewController,
        (c) => new ProductFullViewController(c.get(serviceNames.productFullService))
    )
    .register(serviceNames.zoomViewBaseController, (c) => new ZoomViewBaseController())
    .register(
        serviceNames.productZoomViewController,
        (c) => new ProductZoomViewController(c.get(serviceNames.zoomViewBaseController))
    )
    .register(
        serviceNames.productZoomButtonViewController,
        (c) => new ProductZoomButtonViewController(c.get(serviceNames.zoomViewBaseController))
    )
    .register(serviceNames.hoverZoom, (c) => new HoverZoom())
    .register(
        serviceNames.productHoverZoomViewController,
        (c) => new ProductHoverZoomViewController(c.get(serviceNames.hoverZoom))
    )
    .register(
        serviceNames.productHoverZoomButtonViewController,
        (c) => new ProductHoverZoomButtonViewController(c.get(serviceNames.hoverZoom))
    )
    .register(
        serviceNames.productHoverZoomItemViewController,
        (c) => new ProductHoverZoomItemViewController(c.get(serviceNames.hoverZoom))
    )
    .register(serviceNames.productGridSettings, (c) => new ProductGridSettings())
    .register(
        serviceNames.productFiltersViewController,
        diContainer.factory((c) => new ProductFiltersViewController())
    )
    .register(
        serviceNames.productBriefViewController,
        diContainer.factory(
            (c) =>
                new ProductBriefViewController(
                    c.get(serviceNames.productRepository),
                    c.get(serviceNames.queryFragments)
                )
        )
    )
    .register(serviceNames.configRepository, (c) => new ConfigRepository())
    .register(serviceNames.config, (c) => new Config(c.config))
    .register(
        serviceNames.businessUnitIdParser,
        diContainer.factory((c) => {
            return new BusinessUnitIdParser(c.config.businessUnitId);
        })
    )
    .register(
        serviceNames.queryFragments,
        diContainer.factory((c) => {
            const { config } = c.get(serviceNames.config);

            const queryFragmentsConfig = {
                features: config.features
            };

            return new QueryFragments(queryFragmentsConfig);
        })
    )
    .register(
        serviceNames.productCTAViewController,
        diContainer.factory(
            (c) =>
                new ProductCTAViewController(
                    c.get(serviceNames.productGridService),
                    c.get(serviceNames.productRepository),
                    c.get(serviceNames.dateFormatter),
                    c.get(serviceNames.queryFragments)
                )
        )
    )
    .register(
        serviceNames.productCrossSellViewController,
        diContainer.factory(
            (c) =>
                new ProductCrossSellViewController(
                    c.get(serviceNames.productRepository),
                    c.get(serviceNames.queryFragments)
                )
        )
    )
    .register(
        serviceNames.productQuickViewController,
        diContainer.factory((c) => new ProductQuickViewController())
    )
    .register(
        serviceNames.productGridViewController,
        diContainer.factory(
            (c) =>
                new ProductGridViewController(
                    c.get(serviceNames.productGridSettings),
                    c.get(serviceNames.productGridService)
                )
        )
    )
    .register(
        serviceNames.productGridService,
        diContainer.factory(
            (c) =>
                new ProductGridService(
                    c.get(serviceNames.filterService),
                    c.get(serviceNames.productSortingService),
                    c.get(serviceNames.productRepository),
                    c.get(serviceNames.queryFragments)
                )
        )
    )
    .register(
        serviceNames.productSortingService,
        diContainer.factory(
            (c) =>
                new ProductSortingService(
                    c.get(serviceNames.productGridSettings),
                    c.get(serviceNames.productRepository)
                )
        )
    )
    .register(
        serviceNames.productFullService,
        diContainer.factory(
            (c) =>
                new ProductFullService(
                    c.get(serviceNames.productRepository),
                    c.get(serviceNames.queryFragments)
                )
        )
    )
    .register(serviceNames.productShadesSidebarViewController, (c) => {
        return new ProductShadesSidebarViewController();
    })
    .register(serviceNames.productRepository, (c) => {
        const logger = c.get(serviceNames.logger);

        return new ProductRepository(
            {
                prodcatApiSdk: c.get(serviceNames.prodcatApiSdk),
                inventoryRepository: c.get(serviceNames.inventoryRepository)
            },
            logger
        );
    })
    .register(serviceNames.inventoryRepository, () => {
        return new InventoryRepository();
    })
    .register(
        serviceNames.cartLimitReachedModalController,
        (c) => new CartLimitReachedModalController()
    )
    .register(serviceNames.prodcatApiSdk, (c) => {
        const configStore = c.get(serviceNames.config);
        const baseUrl = configStore.config.baseUrl;
        const businessUnit = configStore.config.businessUnitId;
        const productDisplayEnvs = configStore.config.productDisplayEnvs;
        const clientId = configStore.config.clientId || 'stardust-fe-client';
        const config = {
            baseUrl,
            businessUnit,
            clientId,
            productDisplayEnvs
        };
        const logger = c.get(serviceNames.logger);

        return new ProdcatApiSdk(config, logger);
    })
    .register(serviceNames.productSkuRepository, (c) => {
        return new ProductSkuRepository({
            prodcatApiSdk: c.get(serviceNames.prodcatApiSdk),
            config: c.get(serviceNames.config)
        });
    })
    .register(
        serviceNames.serviceBusController,
        (c) =>
            new ServiceBusController(
                c.get(serviceNames.config),
                c.get(serviceNames.productRepository),
                c.get(serviceNames.productGridSettings),
                c.get(serviceNames.productFullService),
                c.get(serviceNames.productGridService)
            )
    )
    .register(
        serviceNames.dateFormatter,
        diContainer.factory((c) => {
            const { locale } = c.get(serviceNames.businessUnitIdParser);

            const dateFormatterConfig = { locale };

            return new DateFormatter(dateFormatterConfig);
        })
    )
    .register(
        serviceNames.priceFormatter,
        diContainer.factory((c) => {
            const businessUnitIdParser = c.get(serviceNames.businessUnitIdParser);
            const { config } = c.get(serviceNames.config);
            const { priceFormatterSettings = {} } = config;
            const { numberOfDecimals, decimalSeparator, hideZeroDecimals } = priceFormatterSettings;

            const priceFormatterConfig = {
                currency: config.currency,
                locale: businessUnitIdParser.locale,
                numberOfDecimals,
                decimalSeparator,
                hideZeroDecimals
            };

            try {
                return new PriceFormatter(priceFormatterConfig);
            } catch (error) {
                c.get(serviceNames.logger).error({
                    triggerType: TriggerType.runtimeError,
                    message: JSON.stringify(priceFormatterConfig),
                    payload: {
                        error
                    }
                });

                throw error;
            }
        })
    )
    .register(serviceNames.productService, (c) => new ProductService(serviceInitializer))
    .register(
        serviceNames.filterService,
        diContainer.factory((c) => new FilterService())
    )
    .register(
        serviceNames.productInstallmentsPriceViewController,
        diContainer.factory(
            (c) => new ProductInstallmentsPriceViewController(c.get(serviceNames.priceFormatter))
        )
    )
    .register(
        serviceNames.productEngravingViewController,
        (c) => new ProductEngravingViewController()
    )
    .register(
        serviceNames.productShadePickerViewController,
        diContainer.factory(
            (c) => new ProductShadePickerViewController(c.get(serviceNames.filterService))
        )
    )
    .register(
        serviceNames.productShadePickerComponentViewController,
        diContainer.factory((c) => new ProductShadePickerComponentViewController())
    )
    .register(
        serviceNames.productBasicInfoViewController,
        diContainer.factory(
            (c) =>
                new ProductBasicInfoViewController(
                    c.get(serviceNames.productRepository),
                    c.get(serviceNames.queryFragments)
                )
        )
    )
    .register(
        serviceNames.productImagesSliderViewController,
        diContainer.factory((c) => new ProductImageSliderViewController())
    )
    .register(
        serviceNames.productSizePickerViewController,
        diContainer.factory((c) => new ProductSizePickerViewController())
    )
    .register(
        serviceNames.comparisonItemViewController,
        diContainer.factory((c) => new ComparisonItemViewController())
    )
    .register(
        serviceNames.productComparisonModuleViewController,
        diContainer.factory(
            (c) =>
                new ProductComparisonModuleViewController(
                    c.get(serviceNames.productRepository),
                    c.get(serviceNames.queryFragments),
                    c.get(serviceNames.productGridService)
                )
        )
    )
    .register(serviceNames.urlFormatter, (c) => {
        const { config } = c.get(serviceNames.config);
        const urlFormatterConfig = {
            langPrefix: config.drupal?.languagePathPrefix || config.domainLangPrefix
        };

        return new UrlFormatter(urlFormatterConfig);
    })
    .register(serviceNames.stickyContainerFeatureViewController, (c) => {
        return new StickyWrapperViewController();
    })
    .register(
        serviceNames.skuPickerViewController,
        diContainer.factory((c) => new SkuPickerViewController(c.get(serviceNames.filterService)))
    )
    .register(
        serviceNames.shadeFiltersViewController,
        diContainer.factory((c) => new ShadeFiltersViewController())
    );

const { lazyInject } = getDecorators(diContainer);

export { lazyInject };
